import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import { useMutation, useApolloClient } from '@apollo/client';
import { UPDATE_BANNER_TALENT } from '@gql/talents';

interface TalentBannerProps {
    talentId?: string;
    bannerUrl?: string;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) => reject(error));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not create canvas context');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height,
    );
    return new Promise((resolve) => {
        canvas.toBlob((blob) => { if (blob) resolve(blob); }, 'image/jpeg', 0.95);
    });
};

const DropzoneArea: React.FC<{ onDrop: (file: File) => void }> = ({ onDrop }) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: (accepted) => { if (accepted.length > 0) onDrop(accepted[0]); },
        accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
        multiple: false,
    });
    return (
        <Box
            {...getRootProps()}
            sx={(theme) => ({
                width: '100%',
                height: 200,
                border: '2px dashed',
                borderColor: isDragActive ? theme.palette.primary.main : theme.palette.border?.main || theme.palette.divider,
                borderRadius: '8px',
                backgroundColor: isDragActive ? theme.palette.action.hover : theme.palette.background.paper,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                transition: 'all 0.2s ease',
            })}
        >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" align="center">
                {isDragActive ? 'Déposez la bannière ici...' : 'Glissez-déposez une bannière ou cliquez pour sélectionner'}
            </Typography>
        </Box>
    );
};

export const TalentBanner: React.FC<TalentBannerProps> = ({ talentId, bannerUrl }) => {
    const apolloClient = useApolloClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [localBannerUrl, setLocalBannerUrl] = useState<string | undefined>(bannerUrl);

    const [updateBanner, { loading: uploading }] = useMutation(UPDATE_BANNER_TALENT);

    const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleFileDrop = (file: File) => {
        if (!file.type.startsWith('image/')) {
            setUploadError('Le fichier doit être une image');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setImageSrc(reader.result as string);
            setUploadError(null);
        };
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        if (!imageSrc || !croppedAreaPixels) return;
        try {
            const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            const croppedFile = new File([croppedBlob], 'banner.jpg', { type: 'image/jpeg' });

            const { data } = await updateBanner({
                variables: { talentId, file: croppedFile },
            });

            const newUrl: string | undefined = data?.updateBannerTalent;
            if (newUrl) {
                setLocalBannerUrl(newUrl);
                // Update Apollo cache so the talent's profileBanner reflects the new URL
                apolloClient.cache.modify({
                    id: apolloClient.cache.identify({ __typename: 'Talent', _id: talentId }),
                    fields: {
                        profileBanner: (existing: any) => ({ ...existing, url: newUrl }),
                        photo: () => newUrl,
                    },
                });
            }

            setDialogOpen(false);
            setImageSrc(null);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setUploadError(null);
        } catch (err: any) {
            setUploadError(err?.message || "Échec du téléchargement de la bannière");
        }
    };

    const handleClose = () => {
        setDialogOpen(false);
        setImageSrc(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setUploadError(null);
    };

    const effectiveBannerUrl = localBannerUrl ?? bannerUrl;

    return (
        <>
            <Box
                sx={{
                    padding: '30px',
                    position: 'relative',
                    overflow: 'hidden',
                    height: '250px',
                    backgroundColor: "#0C0D18",
                    marginBottom: '56px',
                    flex: 1,
                    width: '100%',
                    mx: -2,
                    cursor: 'pointer',
                }}
                onClick={() => setDialogOpen(true)}
            >
                {/* Banner image or gradient background */}
                {effectiveBannerUrl ? (
                    <Box
                        component="img"
                        src={effectiveBannerUrl}
                        alt="Bannière du talent"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                ) : (
                    /* Decorative blurred ellipse */
                    <Box
                        style={{
                            borderRadius: "528.868px",
                            background: "radial-gradient(196.39% 89.11% at 5.51% 14.84%, rgba(11, 31, 84, 0.20) 0%, rgba(52, 45, 173, 0.40) 27.88%, rgba(83, 64, 191, 0.80) 57.21%, #B684E7 100%)",
                            filter: "blur(17.25px)",
                            width: "528.868px",
                            height: "430.858px",
                            transform: "rotate(6.152deg)",
                            position: "absolute",
                            bottom: "-190px",
                            right: "-200px",
                        }}
                    />
                )}

                {/* Edit overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                        '&:hover': { opacity: 1 },
                    }}
                >
                    <Tooltip title="Modifier la bannière">
                        <IconButton
                            sx={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' }}
                            onClick={(e) => { e.stopPropagation(); setDialogOpen(true); }}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Text and Icon */}
                {!effectiveBannerUrl && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '15.26px',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-end',
                            position: 'relative',
                            height: '100%',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '27.67px',
                                fontWeight: 600,
                                lineHeight: '41.51px',
                                color: '#fff',
                            }}
                        >
                            Create magic with design
                        </Typography>
                        <i className="fi fi-br-magic-wand" style={{ fontSize: '24.42px', color: '#fff', display: 'flex' }} />
                    </Box>
                )}
            </Box>

            {/* Upload Dialog */}
            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>Modifier la bannière</DialogTitle>
                <DialogContent>
                    {!imageSrc ? (
                        <DropzoneArea onDrop={handleFileDrop} />
                    ) : (
                        <Box sx={{ position: 'relative', width: '100%', height: 400 }}>
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={16 / 9}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        </Box>
                    )}
                    {uploadError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {uploadError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    {imageSrc && (
                        <Button onClick={() => setImageSrc(null)} disabled={uploading}>
                            Choisir une autre image
                        </Button>
                    )}
                    <Button onClick={handleClose} disabled={uploading} variant="text" color="secondary">
                        Annuler
                    </Button>
                    {imageSrc && (
                        <Button
                            variant="contained"
                            onClick={handleUpload}
                            disabled={uploading || !croppedAreaPixels}
                            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : undefined}
                        >
                            {uploading ? 'Téléchargement...' : 'Enregistrer'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};
